import apiConfig from "app/api/apiConfig.json";
import { getLocalApi } from "./localApi";
import {
    AgentCommissionRateQueryDto,
    AgentCommissionRateQueryResult,
    AgentCommissionRateQueryStatus,
    SaveAgentCommissionDto,
    AgentCommissionVersionDto,
    AgentCommissionDto,
    AgentCommissionStatus
} from "app/shared/model/AgentCommissionDto";

const withDivision = (template: string, divisionId: number) =>
    template.replace("{divisionId}", encodeURIComponent(String(divisionId)));

const withScope = (template: string, scopeId: string) =>
    template.replace("{scopeId}", encodeURIComponent(scopeId));

export async function getPublishedAgentCommission(scopeId: string, divisionId: number) {
    const localApi = await getLocalApi();

    const url = withScope(withDivision(apiConfig.agent_commission_get_published_version, divisionId), scopeId);

    return await localApi.fetchList(
        url,
        mapAgentCommissionVersion,
        'Error fetching published agent commission'
    );
}

export async function getLatestAgentCommission(scopeId: string, divisionId: number) {
    const localApi = await getLocalApi();

    const url = withScope(withDivision(apiConfig.agent_commission_get_latest, divisionId), scopeId);

    return await localApi.fetchSingle(
        url,
        mapAgentCommission,
        'Error fetching latest agent commission'
    );
}

function toLocalDateString(input: string | Date | null | undefined): string | null {
    if (input == null) return null;
    const s = String(input).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;

    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

export async function putAgentCommissionRates(scopeId: string, divisionId: number, dto: SaveAgentCommissionDto): Promise<void> {
    const localApi = await getLocalApi();

    const payload = {
        ...dto,
        startDate: toLocalDateString((dto as any).startDate),
        endDate: toLocalDateString((dto as any).endDate) ?? undefined,
    };

    const url = withScope(withDivision(apiConfig.agent_commission_get_rates, divisionId), scopeId);

    const response = await localApi.call(url, 'PUT', JSON.stringify(payload));
    if (!response?.ok) {
        const text = await response?.text?.().catch(() => '') ?? '';
        throw new Error(text || `Failed to save agent commission rates (HTTP ${response?.status})`);
    }
}

export async function findRate(divisionId: number, query: AgentCommissionRateQueryDto): Promise<AgentCommissionRateQueryResult> {
    const localApi = await getLocalApi();

    const url = withDivision(apiConfig.agent_commission_find_rate, divisionId);

    return await localApi.call(url, 'POST', JSON.stringify(query))
        .then(async (response) => {
            if (!response?.ok) {
                return { status: AgentCommissionRateQueryStatus.Errored };
            }

            const dtoObj: any = await response.json();
            dtoObj.status = AgentCommissionRateQueryStatus.Found; // <-- ВАЖНО: status (не Status)
            return dtoObj as AgentCommissionRateQueryResult;
        });
}

function mapAgentCommissionStatus(status: unknown): AgentCommissionStatus {
    if (typeof status === 'string') {
        switch (status) {
            case 'Active':
                return AgentCommissionStatus.Active;
            case 'Expired':
                return AgentCommissionStatus.Expired;
            case 'FutureStart':
                return AgentCommissionStatus.FutureStart;
            case 'New':
                return AgentCommissionStatus.New;
        }
    }

    if (typeof status === 'number' && Number.isInteger(status)) {
        switch (status) {
            case 0:
                return AgentCommissionStatus.New;
            case 1:
                return AgentCommissionStatus.Active;
            case 2:
                return AgentCommissionStatus.Expired;
            case 3:
                return AgentCommissionStatus.FutureStart;
        }
    }

    return AgentCommissionStatus.New;
}

function mapAgentCommission(dto: any): AgentCommissionDto {
    return {
        ...dto,
        status: mapAgentCommissionStatus(dto?.status),
    };
}

function mapAgentCommissionVersion(dto: any): AgentCommissionVersionDto {
    return {
        ...dto,
        agentCommission: dto?.agentCommission ? mapAgentCommission(dto.agentCommission) : dto?.agentCommission,
    };
}